import commaNumber from 'comma-number';
import type { ReactNode } from 'react';

import Typo from '@/components/Typography/Typo';
import DashboardCardContentHeader from '@/features/Dashboard/components/DashboardCardContent/DashboardCardContentHeader';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type FeedbackGalleryHeaderProps = {
  title: string;
  sortBy: string;
  orderBy: string;
  startDate?: string;
  endDate?: string;
  isDateOverridden?: boolean;
  count?: number;
  isLoading?: boolean;
  rightAction?: ReactNode;
  onTitleClick?: () => void;
};

const FeedbackGalleryHeader = ({
  title,
  sortBy,
  orderBy,
  startDate,
  endDate,
  isDateOverridden = false,
  count,
  isLoading,
  rightAction,
  onTitleClick,
}: FeedbackGalleryHeaderProps) => {
  const sortOption = FEEDBACK_SORT_OPTIONS.find((opt) => opt.sortKey === sortBy);
  const sortLabel = sortOption?.sortName || sortBy;
  const orderLabel = orderBy === 'desc' ? 'Desc' : 'Asc';
  const formattedCount = count !== undefined ? commaNumber(count) : undefined;
  const fullDateLabel =
    isDateOverridden && startDate && endDate
      ? `${getTimezoneDayjs(startDate).format(DATE_FORMAT.readable)} - ${getTimezoneDayjs(endDate).format(DATE_FORMAT.readable)}`
      : 'Last 12 months';
  const compactDateLabel =
    isDateOverridden && startDate && endDate
      ? `${getTimezoneDayjs(startDate).format(DATE_FORMAT.short)} - ${getTimezoneDayjs(endDate).format(DATE_FORMAT.short)}`
      : 'Last 12 months';
  const subtitleText = `${sortLabel} ${orderLabel} · ${compactDateLabel}`;
  const subtitleTitle = `Sort: ${sortLabel} ${orderLabel} · Date: ${fullDateLabel}`;

  return (
    <DashboardCardContentHeader
      title={
        <Typo type="body0" weight="m" multilineEllipsis={2}>
          {title}
          {formattedCount !== undefined ? ` (${formattedCount})` : ''}
        </Typo>
      }
      titleExtra={
        isLoading ? (
          <Typo type="label5" className="shrink-0 text-gray-400">
            Loading...
          </Typo>
        ) : undefined
      }
      subtitle={
        <Typo
          type="body3"
          className="truncate text-gray-500"
          title={subtitleTitle}
        >
          {subtitleText}
        </Typo>
      }
      rightAction={rightAction}
      rightActionClassName="pt-0.5"
      onTitleClick={onTitleClick}
      headerAlign="start"
    />
  );
};

export default FeedbackGalleryHeader;
